import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ROLE_LABEL, ROLE_BADGE_CLASS } from "@/lib/roles";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Pencil, Trash, X, ArrowRight, EnvelopeSimple, Clock } from "@phosphor-icons/react";

const empty = { code: "", name: "", email: "", role: "perito_interno", active: true, password: "" };
const WRITE_ROLES = new Set(["admin", "gestor_senior"]);

export default function Peritos() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = WRITE_ROLES.has(user?.role);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/peritos");
    setItems(r.data);
  };

  useEffect(() => { load(); }, []);

  const open = (p = null) => {
    setErr("");
    if (p) {
      setEditing(p.id);
      setForm({ ...p, password: "" });
    } else {
      setEditing("new");
      setForm(empty);
    }
  };

  const close = () => { setEditing(null); setForm(empty); setErr(""); };

  const save = async () => {
    setErr("");
    setSaving(true);
    try {
      if (editing === "new") {
        // Nuevo usuario: SIEMPRE se crea por invitación (recibe email para
        // crear su contraseña). El admin no fija contraseña aquí.
        await api.post("/peritos", { ...form, send_invitation: true, password: null });
        toast.success(`Invitación enviada a ${form.email}`);
      } else {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/peritos/${editing}`, payload);
        toast.success("Cambios guardados");
      }
      await load();
      close();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const resendInvitation = async (p, e) => {
    e?.stopPropagation();
    if (!confirm(`Reenviar invitación a ${p.email}?`)) return;
    try {
      const r = await api.post(`/peritos/${p.id}/resend-invitation`);
      if (r.data?.email_sent) {
        toast.success(`Invitación reenviada a ${p.email}`);
      } else {
        toast.warning("Invitación regenerada, pero el email no se pudo enviar (revisa configuración SMTP).");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error reenviando invitación");
    }
  };

  const del = async (p) => {
    if (!confirm(`¿Eliminar a ${p.name}?`)) return;
    try {
      await api.delete(`/peritos/${p.id}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div data-testid="peritos-page">
      <header className="h-14 border-b border-border bg-white px-6 flex items-center justify-between sticky top-0 z-20">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted">Administración</div>
          <h1 className="font-display font-bold text-lg tracking-tight">Usuarios · {items.length}</h1>
        </div>
        <button onClick={() => open()} data-testid="new-perito-button" className="bg-foreground text-white text-xs px-3 py-1.5 rounded-md font-medium inline-flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" weight="bold" /> Nuevo usuario
        </button>
      </header>

      <div className="p-6">
        <div className="border border-[#E8EAED] rounded-[10px] bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="thead-modern">
              <tr>
                <th>Código</th>
                <th>Nombre</th>
                <th>Email</th>
                <th>Rol</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p, idx) => (
                <tr
                  key={p.id}
                  className={`border-t border-[#F1F3F5] hover:bg-[#F5F6F8] cursor-pointer group transition ${idx % 2 ? "bg-[#FBFBFC]" : ""}`}
                  onClick={() => navigate(`/admin/peritos/${p.id}`)}
                  data-testid={`row-perito-${p.code}`}
                >
                  <td className="py-2.5 px-4 text-[12px] tabular-nums">{p.code}</td>
                  <td className="py-2.5 px-4 font-medium">{p.name}</td>
                  <td className="py-2.5 px-4 text-muted">{p.email}</td>
                  <td className="py-2.5 px-4">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${ROLE_BADGE_CLASS[p.role] || "bg-surface text-muted"}`}>
                      {ROLE_LABEL[p.role] || p.role}
                    </span>
                  </td>
                  <td className="py-2.5 px-4">
                    {p.invitation_pending ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium border bg-amber-50 text-amber-800 border-amber-200 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" weight="bold" /> Pendiente invitación
                      </span>
                    ) : (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${p.active ? "bg-status-success-bg text-status-success-fg border-status-success-bd" : "bg-status-error-bg text-status-error-fg border-status-error-bd"}`}>
                        {p.active ? "Activo" : "Inactivo"}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 px-4 text-right whitespace-nowrap">
                    {p.invitation_pending && canEdit && (
                      <button
                        onClick={(e) => resendInvitation(p, e)}
                        data-testid={`resend-invitation-${p.code}`}
                        className="p-1 text-muted hover:text-foreground"
                        title="Reenviar invitación"
                      >
                        <EnvelopeSimple className="w-3.5 h-3.5" weight="bold" />
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); open(p); }} data-testid={`edit-perito-${p.code}`} className="p-1 text-muted hover:text-foreground"><Pencil className="w-3.5 h-3.5" weight="bold" /></button>
                    <button onClick={(e) => { e.stopPropagation(); del(p); }} data-testid={`delete-perito-${p.code}`} className="p-1 text-muted hover:text-status-error-fg ml-1"><Trash className="w-3.5 h-3.5" weight="bold" /></button>
                    <ArrowRight className="w-3.5 h-3.5 inline ml-2 text-muted opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" weight="bold" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={close}>
          <div className="bg-white rounded-lg w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="perito-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg tracking-tight">{editing === "new" ? "Nuevo usuario" : "Editar usuario"}</h2>
              <button onClick={close} className="text-muted"><X className="w-4 h-4" weight="bold" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label>Código</label>
                <input type="text" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="perito-code" />
              </div>
              <div>
                <label>Nombre</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="perito-name" />
              </div>
              <div>
                <label>Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="perito-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label>Rol</label>
                  <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="perito-role">
                    <option value="admin">{ROLE_LABEL.admin}</option>
                    <option value="gestor_senior">{ROLE_LABEL.gestor_senior}</option>
                    <option value="gestor_siniestros">{ROLE_LABEL.gestor_siniestros}</option>
                    <option value="perito_interno">{ROLE_LABEL.perito_interno}</option>
                    <option value="perito_externo">{ROLE_LABEL.perito_externo}</option>
                    <option value="tramitador_telematico">{ROLE_LABEL.tramitador_telematico}</option>
                  </select>
                </div>
                <div>
                  <label>Estado</label>
                  <select value={form.active ? "1" : "0"} onChange={(e) => setForm({ ...form, active: e.target.value === "1" })} data-testid="perito-active">
                    <option value="1">Activo</option>
                    <option value="0">Inactivo</option>
                  </select>
                </div>
              </div>
              {editing === "new" ? (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-[12px] text-amber-900 flex items-start gap-2">
                  <EnvelopeSimple className="w-4 h-4 mt-0.5 flex-shrink-0" weight="bold" />
                  <div>
                    Le enviaremos un email a <strong>{form.email || "este correo"}</strong> con un
                    enlace para que cree su propia contraseña. El enlace caduca en 48 horas.
                  </div>
                </div>
              ) : (
                <div>
                  <label>Nueva contraseña (vacío = sin cambio)</label>
                  <input type="text" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="perito-password" placeholder="Ej. El sol de los 40" />
                  <div className="text-[10px] text-muted mt-1">
                    Si la cambias aquí, debe cumplir la política: frase con mayúscula y nº de 2+ cifras.
                  </div>
                </div>
              )}
              {err && <div className="text-xs text-status-error-fg bg-status-error-bg border border-status-error-bd px-2 py-1.5 rounded">{err}</div>}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={close} className="text-xs px-4 py-2 text-muted hover:text-foreground">Cancelar</button>
              <button onClick={save} disabled={saving} data-testid="save-perito" className="bg-foreground text-white text-xs px-4 py-2 rounded-md font-medium disabled:opacity-50">
                {saving ? "Guardando..." : (editing === "new" ? "Enviar invitación" : "Guardar")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
